import React, { useState, useEffect, useMemo } from "react";
import { message, Alert, Checkbox, InputNumber, Statistic, Row, Col, Typography } from "antd";
import CriticalActionModal from "./CriticalActionModal";
import { modifyPaidEcheance } from "../api/subscriptions/subscriptionsRequest";

const { Text } = Typography;

const fmt = (v) =>
  v === null || v === undefined || isNaN(Number(v))
    ? "—"
    : `${Number(v).toFixed(2)} DH`;

/**
 * Feature 4 — Modify an already PAID echeance amount.
 * Δ = new − old is cascaded to the payment/transaction and (optionally) the
 * collecting agent's balance (balance += Δ). Displays the running balance and a
 * prominent banner if the agent balance would go negative.
 *
 * `agentBalance` is optional: when the panel can resolve the collecting agent's
 * balance it is passed in for a live projection; otherwise only Δ is shown.
 */
const detailTypeLabel = (t) =>
  ({
    echeance: "Échéance",
    registration_fee: "Frais d'inscription",
    reactivation_fee: "Frais de réactivation",
    macaron_fee: "Frais de macaron",
  }[t] || t);

const ModifyPaidEcheanceModal = ({
  visible,
  onCancel,
  onSuccess,
  echeance,
  agentBalance = null,
  breakdown = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [newAmount, setNewAmount] = useState(0);
  const [updateAgentSolde, setUpdateAgentSolde] = useState(true);

  const oldAmount = Number(echeance?.amount ?? 0);

  useEffect(() => {
    if (visible && echeance) {
      setNewAmount(Number(echeance.amount ?? 0));
      setUpdateAgentSolde(true);
    }
  }, [visible, echeance]);

  const delta = useMemo(() => {
    const n = Number(newAmount);
    if (isNaN(n)) return 0;
    return Number((n - oldAmount).toFixed(2));
  }, [newAmount, oldAmount]);

  const amountValid =
    newAmount !== null && newAmount !== undefined && !isNaN(Number(newAmount)) && Number(newAmount) >= 0;

  const hasBalance = agentBalance !== null && agentBalance !== undefined && !isNaN(Number(agentBalance));
  const projectedBalance = useMemo(() => {
    if (!hasBalance || !updateAgentSolde) return hasBalance ? Number(agentBalance) : null;
    return Number((Number(agentBalance) + delta).toFixed(2));
  }, [hasBalance, agentBalance, delta, updateAgentSolde]);

  const willGoNegative = updateAgentSolde && projectedBalance !== null && projectedBalance < 0;

  const handleConfirm = async (reason) => {
    if (!echeance) return;
    setLoading(true);
    try {
      const response = await modifyPaidEcheance(echeance.id, {
        new_amount: Number(newAmount),
        reason,
        update_agent_solde: updateAgentSolde,
      });
      if (response?.success) {
        message.success(response.message || "Échéance corrigée avec succès");
        onSuccess?.(response.data);
      } else if (response?.message === "Aucun changement détecté") {
        message.info("Aucun changement détecté");
        onCancel?.();
      } else {
        message.error(response?.message || response?.error || "Erreur lors de la correction");
      }
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || "Erreur lors de la correction de l'échéance");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CriticalActionModal
      visible={visible}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      loading={loading}
      title="Corriger le montant d'une échéance payée"
      okText="Appliquer la correction"
      extraValid={amountValid}
      summaryItems={[
        { label: "Échéance", to: `#${echeance?.id ?? ""}` },
        { label: "Montant", from: fmt(oldAmount), to: fmt(Number(newAmount)) },
        {
          label: "Différence (Δ)",
          to: `${delta > 0 ? "+" : ""}${delta.toFixed(2)} DH`,
        },
      ]}
      warningBanner={
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="Correction d'une échéance déjà payée"
            description="Le montant sera répercuté sur le détail de paiement, le paiement, la transaction et le solde de l'agent collecteur."
          />
          {willGoNegative && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message="Attention : solde agent négatif"
              description={`Cette action laissera l'agent avec un solde négatif de ${fmt(projectedBalance)}.`}
            />
          )}
        </>
      }
    >
      {Array.isArray(breakdown) && breakdown.length > 1 && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 8 }}
          message="Ce paiement contient plusieurs lignes"
          description={
            <div>
              {breakdown.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: d.type === "echeance" ? 600 : 400,
                  }}
                >
                  <span>
                    {detailTypeLabel(d.type)}
                    {d.type === "echeance" ? " (ligne corrigée)" : ""}
                  </span>
                  <span>{fmt(d.amount)}</span>
                </div>
              ))}
              <div style={{ marginTop: 4, fontSize: 12 }}>
                Seule la ligne « Échéance » est corrigée ; les autres frais restent inchangés.
              </div>
            </div>
          }
        />
      )}

      <div style={{ marginTop: 12 }}>
        <Text strong>Nouveau montant</Text>
        <InputNumber
          style={{ width: "100%", marginTop: 6 }}
          value={newAmount}
          onChange={setNewAmount}
          min={0}
          step={0.01}
          precision={2}
          status={!amountValid ? "error" : ""}
          addonAfter="DH"
        />

        <Checkbox
          checked={updateAgentSolde}
          onChange={(e) => setUpdateAgentSolde(e.target.checked)}
          style={{ marginTop: 12 }}
        >
          Ajuster le solde de l'agent collecteur ({delta >= 0 ? "+" : ""}
          {delta.toFixed(2)} DH)
        </Checkbox>

        {hasBalance ? (
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={12}>
              <Statistic title="Solde actuel de l'agent" value={Number(agentBalance)} precision={2} suffix="DH" />
            </Col>
            <Col span={12}>
              <Statistic
                title="Solde après l'action"
                value={projectedBalance ?? Number(agentBalance)}
                precision={2}
                suffix="DH"
                valueStyle={{ color: willGoNegative ? "#cf1322" : undefined }}
              />
            </Col>
          </Row>
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="Le solde de l'agent collecteur sera ajusté côté serveur de la différence (Δ) si l'option est cochée."
          />
        )}
      </div>
    </CriticalActionModal>
  );
};

export default ModifyPaidEcheanceModal;
